import React, { useCallback, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import { Button } from 'ksnm-common-ui/lib/Button';
import { Icon } from 'ksnm-common-ui/lib/Icon';
import { Select } from 'ksnm-common-ui/lib/Select';
import { sortBy, uniq } from 'lodash-es';

import { fetchReferences } from '@stores/dictionaryStores/fetchReferences';
import { organizationsStore } from '@stores/dictionaryStores/organizationsStore';
import { periodsStore } from '@stores/dictionaryStores/periodsStore';
import { reportingFormsStore } from '@stores/reportingFormsStore';
import { reportingTreeStore } from '@stores/reportingTreeStore';
import type { TreeViewInterface } from '@stores/reportingTreeStore/toViewModel';

import styles from './Filter.module.scss';

export const Filter = observer((): JSX.Element => {
  const { organizations } = organizationsStore;
  const { periods } = periodsStore;
  const { removePage } = reportingFormsStore;
  const { reportingTree, clear, setOrganizationCode, organizationCode, filters, setFilters } = reportingTreeStore;

  useEffect(() => {
    fetchReferences(['organizations', 'periods']);
  }, []);

  const yearItems = useMemo(() => {
    const output: string[] = [];
    reportingTree.forEach((company) => company?.children?.forEach((year) => year.name && output.push(year.name)));
    return output;
  }, [reportingTree]);

  const groupItems = useMemo(() => {
    const output: string[] = [];
    reportingTree.forEach(
      (company) =>
        company?.children?.forEach((year) => {
          if (filters.year && filters.year !== year.name) return;
          return year?.children?.forEach((group) => group.name && output.push(group.name));
        }),
    );
    return sortBy([...new Set(output)]);
  }, [filters.year, reportingTree]);

  const periodItems = useMemo(() => {
    let output: TreeViewInterface[] = [];
    reportingTree.forEach(
      (company) =>
        company?.children?.forEach(
          (year) =>
            year?.children?.forEach((group) => group?.children?.forEach((packageItem) => output.push(packageItem))),
        ),
    );

    if (filters.year) {
      output = output.filter((item) => `${item.reportPackageYear}` === filters.year);
    }

    if (filters.group) {
      const group = filters.group.split(' - ')[0];
      output = output.filter((item) => item.reportGroupCode === group);
    }

    return uniq(
      sortBy(output, 'periodCode').map((item) => {
        const period = periods.find((el) => el.code === item.periodCode);
        return `${period?.code} - ${period?.name}`;
      }),
    );
  }, [filters.group, filters.year, periods, reportingTree]);

  const packageItems = useMemo(() => {
    let output: TreeViewInterface[] = [];
    reportingTree.forEach(
      (company) =>
        company?.children?.forEach(
          (year) =>
            year?.children?.forEach((group) => group?.children?.forEach((packageItem) => output.push(packageItem))),
        ),
    );

    if (filters.year) {
      output = output.filter((item) => `${item.reportPackageYear}` === filters.year);
    }

    if (filters.group) {
      const group = filters.group.split(' - ')[0];
      output = output.filter((item) => item.reportGroupCode === group);
    }

    if (filters.period) {
      const period = filters.period.split(' - ')[0];
      output = output.filter((item) => item.periodCode === period);
    }

    return output.map((item) => item.name ?? '');
  }, [filters.group, filters.period, filters.year, reportingTree]);

  const filtersValue = useMemo(
    () => [
      {
        label: 'Отчетный год',
        value: filters.year,
        options: yearItems,
        width: 160,
        name: 'year',
      },
      {
        label: 'Код группы отчетности',
        value: filters.group,
        options: groupItems,
        width: 220,
        name: 'group',
      },
      {
        label: 'Период',
        value: filters.period,
        options: periodItems,
        width: 200,
        name: 'period',
      },
      {
        label: 'Код пакета отчетов',
        value: filters.package,
        options: packageItems,
        width: 240,
        name: 'package',
      },
    ],
    [filters.group, filters.package, filters.period, filters.year, groupItems, packageItems, periodItems, yearItems],
  );

  const onChange = useCallback(
    (name, value) => {
      if (name === 'year') setFilters({ year: value, package: undefined, group: undefined, period: undefined });
      if (name === 'group') setFilters({ ...filters, group: value, package: undefined, period: undefined });
      if (name === 'period') setFilters({ ...filters, period: value, package: undefined });
      if (name === 'package') setFilters({ ...filters, package: value });
      removePage(true);
    },
    [setFilters, filters, removePage],
  );

  const onChangeOrganization = useCallback(
    (value) => {
      clear();
      removePage(true);
      setOrganizationCode(value);
    },
    [clear, removePage, setOrganizationCode],
  );

  const handleResetFilters = useCallback(() => {
    setFilters({});
  }, [setFilters]);

  const filtersItems = useMemo(
    () =>
      filtersValue.map(({ name, label, options, value, width }) => (
        <div key={label} style={{ width }}>
          <Select
            items={options.map((item) => ({ name: item, id: item }))}
            placeholder={label}
            value={value}
            size="small"
            onChange={(val) => onChange(name, val)}
            showArrow
            showSearch={false}
            allowClear
          />
        </div>
      )),
    [filtersValue, onChange],
  );

  const disabled = useMemo(() => !Object.keys(filters).length, [filters]);

  return (
    <div className={styles.designFilters}>
      <div className={styles.filterItems}>
        <Select
          items={organizations}
          placeholder="Выберите организацию"
          value={organizationCode}
          size="small"
          onChange={onChangeOrganization}
          allowClear={false}
          showSearch
          className={styles.organizationSelect}
        />
        {filtersItems}
      </div>

      <Button onClick={handleResetFilters} disabled={disabled} icon={<Icon name="stop" />}>
        Сбросить фильтры
      </Button>
    </div>
  );
});
